import { ApolloError } from '@apollo/client';
import { CommonButton, ConfirmationModal, LoadingPage, styleClassNames, TModalProps } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { ForwardedRef, forwardRef, MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLayoutContext } from '../../pages/shared/Layout';

import clsx from 'clsx';
import { toast } from 'react-toastify';
import styles from './TablePage.module.scss';

export type TConfirmationProps = Pick<TModalProps, 'confirmLabel' | 'icon' | 'headline' | 'onConfirmClick' | 'open' | 'supportingText'>;

export type TTablePageRef = {
  resetConfirmationModal: () => void;
  setConfirmationProps: (v?: TConfirmationProps) => void;
};

type TTablePageProps = {
  actionsLoading?: boolean;
  children?: ReactNode;
  dataLoading?: boolean;
  error?: ApolloError;
  forwardedRef: ForwardedRef<TTablePageRef>;
  title?: string;
  titleButtonLabel?: string;
  titleButtonOnClick?: MouseEventHandler<HTMLButtonElement>;
};

export const TablePageInner = (props: TTablePageProps) => {
  const { actionsLoading, children, dataLoading, error, forwardedRef, title, titleButtonLabel, titleButtonOnClick } = props;

  const { className, fullGridClassName, titleClassName } = useLayoutContext();

  const [confirmationProps, setConfirmationPropsState] = useState<TConfirmationProps>({});

  const resetConfirmationModal = useCallback(() => {
    setConfirmationPropsState({});
  }, []);

  const setConfirmationProps = useCallback((newProps?: TConfirmationProps) => {
    if (newProps == null) setConfirmationPropsState({});
    else setConfirmationPropsState(_.cloneDeep(newProps));
  }, []);

  useEffect(() => {
    const v: TTablePageRef = {
      resetConfirmationModal,
      setConfirmationProps
    };

    if (typeof forwardedRef === 'function') forwardedRef(v);
    else if (forwardedRef != null) forwardedRef.current = v;
  }, [forwardedRef, resetConfirmationModal, setConfirmationProps]);

  const showTitleButton = useMemo(() => {
    return titleButtonLabel != null && titleButtonOnClick != null;
  }, [titleButtonLabel, titleButtonOnClick]);

  if (dataLoading) return <LoadingPage />;

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    //return <ErrorPage error={error} />;
  }

  return (
    <div className={fullGridClassName}>
      {!_.isEmpty(confirmationProps) && <ConfirmationModal {...confirmationProps} closeModal={resetConfirmationModal} confirmLoading={actionsLoading} onCancelClick={resetConfirmationModal} />}
      <div className={clsx(className, styleClassNames.tablePageTable, styles.contentContainer)}>
        {(title || showTitleButton) && (
          <div className={styleClassNames.tablePageHeader}>
            {title && <h1 className={clsx(titleClassName, styles.title)}>{title}</h1>}
            {showTitleButton && <CommonButton className={styles.headerButton} label={titleButtonLabel || ''} onClick={titleButtonOnClick} variant='filled' />}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

TablePageInner.displayName = 'TablePageInner';

export const TablePage = forwardRef<TTablePageRef, Omit<TTablePageProps, 'forwardedRef'>>((props, ref) => {
  return <TablePageInner {...props} forwardedRef={ref} />;
});

TablePage.displayName = 'TablePage';
